import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useEffect, useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LIMITS, flowNameSchema } from '@/lib/vsl-dinamica/contract';

interface FlowNameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  submitLabel: string;
  initialName?: string;
  pending?: boolean;
  onSubmit: (name: string) => void;
}

// Diálogo de nome usado para criar e para renomear um fluxo.
export function FlowNameDialog({
  open,
  onOpenChange,
  title,
  description,
  submitLabel,
  initialName = '',
  pending = false,
  onSubmit,
}: FlowNameDialogProps) {
  useLanguage();
  const [name, setName] = useState(initialName);

  useEffect(() => {
    if (open) setName(initialName);
  }, [open, initialName]);

  const parsed = flowNameSchema.safeParse(name);
  const unchanged = initialName !== '' && name.trim() === initialName.trim();

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!parsed.success || unchanged || pending) return;
    onSubmit(parsed.data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <form onSubmit={submit} className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>

          <div className="flex flex-col gap-2">
            <Label htmlFor="flow-name">{translateInterfaceText("Nome do fluxo")}</Label>
            <Input
              id="flow-name"
              value={name}
              maxLength={LIMITS.NAME_MAX}
              autoFocus
              placeholder={translateInterfaceText("Ex.: Funil Emagrecimento 30D")}
              onChange={(event) => setName(event.target.value)}
            />
            <span className="text-xs text-[#6B7280]">{translateInterfaceText("Só você vê esse nome.")}</span>
          </div>

          <DialogFooter className="gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="hover:border-[#DC2626] hover:bg-[#DC2626] hover:text-white"
            >
              {translateInterfaceText("Cancelar")}</Button>
            <Button type="submit" disabled={!parsed.success || unchanged || pending}>
              {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
